import React, { Component } from 'react';
import moment from 'moment';
import { isDate } from 'lodash';
import map from 'lodash/fp/map';
import PropTypes from 'prop-types';
import DayPickerInput from 'react-day-picker-v7/DayPickerInput';
import { formatDate, parseDate } from 'react-day-picker-v7/moment';
import Input from './components/Input';
import Button from './components/Button';
import Range from './components/Range';
import Anchor from './components/Anchor';
import config from '../../../config';
import Overlay from './components/Overlay';
import * as presets from './presets';
import withDateFormat from '../../auth/utils/withDateFormat';

const presetsByProps = props => (
  props.presets ? (presets[props.presets] || presets.defaultPresets) : presets.noPresets
);

class DatePicker extends Component {
  static propTypes = {
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    value: PropTypes.string,
    // eslint-disable-next-line react/no-unused-prop-types
    startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    // eslint-disable-next-line react/no-unused-prop-types
    endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    // eslint-disable-next-line react/no-unused-prop-types
    datesMarkedDisabled: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)])),
    daysOfWeekMarkedDisabled: PropTypes.arrayOf(PropTypes.number),
    addClearBtn: PropTypes.bool,
    component: PropTypes.oneOf(['input', 'button', 'anchor']),
    iconSize: PropTypes.oneOf(['bare', 'medium']),
    buttonType: PropTypes.oneOf(['bare', 'blank']),
    range: PropTypes.bool,
    from: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    to: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    anchorLabel: PropTypes.string,
    presets: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    dateFormat: PropTypes.string.isRequired,
    withValue: PropTypes.bool,
    isFixedRange: PropTypes.bool,
    fixedRangeDays: PropTypes.number,
    renderDay: PropTypes.func,
  };

  static defaultProps = {
    asDropdown: false,
    onChange: () => null,
    disabled: false,
    value: '',
    startDate: null,
    endDate: null,
    datesMarkedDisabled: null,
    daysOfWeekMarkedDisabled: null,
    addClearBtn: null,
    component: 'input',
    iconSize: null,
    buttonType: 'bare',
    range: false,
    from: null,
    to: null,
    anchorLabel: null,
    presets: true,
    withValue: false,
    isFixedRange: false,
    fixedRangeDays: 1,
    renderDay: null,
  };

  static parseDate(date) {
    if (isDate(date)) return date;
    const parsed = moment(date, config.dateFormatISO);
    if (parsed.isValid()) return parsed.toDate();
    return null;
  }

  static initialState = {
    value: undefined,
    from: undefined,
    to: undefined,
    lastSelectedPreset: undefined,
    uiSelectedRangeFrom: undefined,
    uiSelectedRangeTo: undefined,
    fromSelected: false,
  };

  static rangeSort(from, to) {
    if (from > to) {
      return { from: to, to: from };
    }
    return { from, to };
  }

  clampSelectedDate(day) {
    let resultDay = moment(day);

    const endDateMoment = this.props.endDate ? moment(this.props.endDate) : null;
    if (endDateMoment && resultDay.isAfter(endDateMoment, 'd')) {
      resultDay = endDateMoment;
    }

    const startDateMoment = this.props.startDate ? moment(this.props.startDate) : null;
    if (startDateMoment && resultDay.isBefore(startDateMoment, 'd')) {
      resultDay = startDateMoment;
    }

    return resultDay.toDate();
  }

  fixedRangeStartingWithDate(startDate) {
    let from = moment(this.clampSelectedDate(startDate));
    let to = moment(from).add(this.props.fixedRangeDays, 'd');

    const endDateMoment = this.props.endDate ? moment(this.props.endDate) : null;
    if (endDateMoment && to.isAfter(endDateMoment, 'd')) {
      to = endDateMoment;
      from = moment(to).subtract(this.props.fixedRangeDays, 'd');
    }

    return { from: from.toDate(), to: to.toDate() };
  }

  fixedRangeEndingWithDate(endDate) {
    let to = moment(this.clampSelectedDate(endDate));
    let from = moment(to).subtract(this.props.fixedRangeDays, 'd');

    const startDateMoment = this.props.startDate ? moment(this.props.startDate) : null;
    if (startDateMoment && from.isBefore(startDateMoment, 'd')) {
      from = startDateMoment;
      to = moment(from).add(this.props.fixedRangeDays, 'd');
    }

    return { from: from.toDate(), to: to.toDate() };
  }

  state = { ...DatePicker.initialState };

  componentDidMount() {
    this.updateSelected();
  }

  componentWillReceiveProps(nextProps) {
    if (
      nextProps.value !== this.props.value
      || nextProps.from !== this.props.from
      || nextProps.to !== this.props.to
    ) this.updateSelected(nextProps);
  }

  getDateProps(props = this.props) {
    const { value, startDate, endDate, from, to, datesMarkedDisabled } = props;
    const datesDisabled = map(DatePicker.parseDate, datesMarkedDisabled);

    return {
      datesMarkedDisabled: datesDisabled,
      value: DatePicker.parseDate(value),
      startDate: DatePicker.parseDate(startDate),
      endDate: DatePicker.parseDate(endDate),
      from: DatePicker.parseDate(from),
      to: DatePicker.parseDate(to),
    };
  }

  getModifiers() {
    const { datesMarkedDisabled } = this.getDateProps();
    const modifiers = {
      'is-disabled': [
        ...datesMarkedDisabled,
        { daysOfWeek: this.props.daysOfWeekMarkedDisabled },
      ],
    };
    if (!this.props.range) return modifiers;
    if (!moment(this.state.uiSelectedRangeFrom).isSame(this.state.uiSelectedRangeTo, 'day')) {
      const { from, to } = DatePicker.rangeSort(this.state.uiSelectedRangeFrom, this.state.uiSelectedRangeTo);
      modifiers.range = {
        after: from,
        before: to,
      };
      modifiers.start = from;
      modifiers.end = to;
    } else {
      modifiers.selected = this.state.uiSelectedRangeTo;
    }
    return modifiers;
  }

  getOverlayProps() {
    return {
      ...this.props,
      from: this.state.uiSelectedRangeFrom,
      to: this.state.uiSelectedRangeTo,
      clearValue: this.clearValue,
      selectPreset: this.selectPreset,
      presets: presetsByProps(this.props),
      selectFrom: this.handleFromInputChange,
      selectTo: this.handleToInputChange,
      hide: this.forceHide,
    };
  }

  key = Date.now();

  forceHide = () => {
    this.dayPickerInput.showDayPicker();
    this.dayPickerInput.hideDayPicker();
  };

  handleDayChange = (day) => {
    if (!this.props.range) {
      this.setState({ value: day });
      this.props.onChange(day);
      return;
    }

    if (this.props.isFixedRange) {
      const { from, to } = this.fixedRangeStartingWithDate(day);

      this.props.onChange(from, to);
      this.setState({
        from,
        to,
        fromSelected: false,
        lastSelectedPreset: undefined,
      });
      this.dayPickerInput.hideDayPicker();
      return;
    }

    if (!this.state.fromSelected) {
      this.setState({
        uiSelectedRangeFrom: day,
        uiSelectedRangeTo: day,
        fromSelected: true,
        lastSelectedPreset: undefined,
      });
      this.dayPickerInput.getInput().focus();
      return;
    }

    const { from, to } = DatePicker.rangeSort(this.state.uiSelectedRangeFrom, day);
    this.props.onChange(from, to);
    this.setState({
      from,
      to,
      fromSelected: false,
      lastSelectedPreset: undefined,
    });
    this.dayPickerInput.hideDayPicker();
  };

  handleFromInputChange = (newFrom) => {
    let from;
    let to;

    if (this.props.isFixedRange) {
      ({ from, to } = this.fixedRangeStartingWithDate(newFrom));
    } else {
      ({ from, to } = DatePicker.rangeSort(this.clampSelectedDate(newFrom), this.state.uiSelectedRangeTo));
    }

    this.props.onChange(from, to);
    this.setState({
      from,
      to,
      uiSelectedRangeFrom: from,
      uiSelectedRangeTo: to,
      fromSelected: false,
      lastSelectedPreset: undefined,
    });
  };

  handleToInputChange = (newTo) => {
    let from;
    let to;

    if (this.props.isFixedRange) {
      ({ from, to } = this.fixedRangeEndingWithDate(newTo));
    } else {
      ({ from, to } = DatePicker.rangeSort(this.state.uiSelectedRangeFrom, this.clampSelectedDate(newTo)));
    }
    this.props.onChange(from, to);
    this.setState({
      from,
      to,
      uiSelectedRangeFrom: from,
      uiSelectedRangeTo: to,
      fromSelected: false,
      lastSelectedPreset: undefined,
    });
  };

  handleDayMouseEnter = (day) => {
    if (!this.props.range || !this.state.fromSelected) return;
    this.setState({
      uiSelectedRangeTo: day,
    });
  };

  handleDayPickerHide = () => {
    this.setState({
      fromSelected: false,
    });
    this.key = Date.now();
  };

  handleDayPickerShow = () => {
    this.setState({
      uiSelectedRangeFrom: this.state.from,
      uiSelectedRangeTo: this.state.to,
    });
  };

  clearValue = () => {
    this.setState({ ...DatePicker.initialState });
    this.props.onChange(null);
    this.dayPickerInput.hideDayPicker();
  };

  selectPreset = key => () => {
    const presetsToUse = presetsByProps(this.props);
    const { from, to } = presetsToUse.getRange(key);
    this.setState({
      from,
      to,
      uiSelectedRangeFrom: from,
      uiSelectedRangeTo: to,
    });
    this.props.onChange(from, to);
    this.setState({ lastSelectedPreset: key });
    this.key = Date.now();
    this.dayPickerInput.hideDayPicker();
  };

  updateSelected(props = this.props) {
    const { value, from, to } = this.getDateProps(props);
    this.setState({
      selectedDay: value,
      from,
      to,
      uiSelectedRangeFrom: from,
      uiSelectedRangeTo: to,
    });
  }

  renderComponent() {
    if (this.props.component === 'anchor') {
      return Anchor;
    }
    if (this.props.range) return Range;
    switch (this.props.component) {
      case 'button':
        return Button;
      case 'input':
      default:
        return Input;
    }
  }

  renderDay(day) {
    return day.getDate();
  }

  render() {
    const {
      startDate,
      endDate,
    } = this.getDateProps();

    return (
      <DayPickerInput
        ref={(ref) => { this.dayPickerInput = ref; }}
        component={this.renderComponent()}
        inputProps={{
          disabled: this.props.disabled,
          iconSize: this.props.iconSize,
          buttonType: this.props.buttonType,
          from: this.state.from,
          to: this.state.to,
          addClearBtn: this.props.addClearBtn,
          overlayProps: this.getOverlayProps(),
          presets: presetsByProps(this.props),
          anchorLabel: this.props.anchorLabel,
          format: this.props.dateFormat,
          lastSelectedPreset: this.state.lastSelectedPreset,
          'data-automation-id': 'filter:datePickerInput',
        }}
        value={this.state.value}
        onDayChange={this.handleDayChange}
        formatDate={formatDate}
        parseDate={parseDate}
        placeholder={`${formatDate(new Date())}`}
        dayPickerProps={{
          month: this.state.value || this.state.from,
          showOutsideDays: true,
          fromMonth: startDate,
          toMonth: endDate,
          selectedDays: this.state.value || this.state.uiSelectedRangeTo,
          disabledDays: [
            {
              before: startDate,
              after: endDate,
            },
          ],
          modifiers: this.getModifiers(),
          fixedWeeks: true,
          onDayMouseEnter: this.handleDayMouseEnter,
          renderDay: this.props.renderDay || this.renderDay,
        }}
        overlayComponent={Overlay}
        hideOnDayClick={!this.props.range}
        onDayPickerHide={this.handleDayPickerHide}
        onDayPickerShow={this.handleDayPickerShow}
        keepFocus={false}
      />
    );
  }
}

export default withDateFormat()(DatePicker);
